import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const MODAL_HISTORY_KEY = "__zeroone_modal_back_guard";
const MODAL_SELECTOR = [
  '[role="dialog"]',
  '[aria-modal="true"]',
  '[data-modal-back="true"]',
].join(",");

const ROUTE_MODAL_PATHS = new Set(["/settings", "/admin/settings", "/superadmin/settings"]);

function isVisible(element) {
  if (!element || !element.isConnected) return false;
  const style = window.getComputedStyle(element);
  return style.display !== "none" && style.visibility !== "hidden" && style.opacity !== "0";
}

function getOpenModals() {
  return Array.from(document.querySelectorAll(MODAL_SELECTOR)).filter(isVisible);
}

function getStackOrder(element) {
  const zIndex = Number.parseInt(window.getComputedStyle(element).zIndex, 10);
  return Number.isFinite(zIndex) ? zIndex : 0;
}

function closeTopModal() {
  const modals = getOpenModals();
  const topModal = modals.sort((a, b) => getStackOrder(b) - getStackOrder(a))[0];

  const closeButton = topModal?.querySelector(
    [
      "[data-modal-close]",
      'button[aria-label^="Close"]',
      'button[aria-label*="close" i]',
      'button[title^="Close"]',
      'button[title*="close" i]',
    ].join(",")
  );

  if (closeButton) {
    closeButton.click();
    return;
  }

  document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
}

export default function useModalBackNavigationGuard() {
  const location = useLocation();
  const hasModalHistoryRef = useRef(false);
  const ignoreNextPopRef = useRef(false);
  const lastPageUrlRef = useRef(typeof window !== "undefined" ? window.location.href : "");

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;
    if (ROUTE_MODAL_PATHS.has(location.pathname)) return undefined;

    lastPageUrlRef.current = window.location.href;

    const syncHistory = () => {
      const hasOpenModal = getOpenModals().length > 0;

      if (hasOpenModal && !hasModalHistoryRef.current) {
        hasModalHistoryRef.current = true;
        lastPageUrlRef.current = window.location.href;
        window.history.pushState(
          { ...(window.history.state || {}), [MODAL_HISTORY_KEY]: true },
          "",
          window.location.href
        );
        return;
      }

      if (!hasOpenModal && hasModalHistoryRef.current) {
        hasModalHistoryRef.current = false;
        if (window.history.state?.[MODAL_HISTORY_KEY]) {
          ignoreNextPopRef.current = true;
          window.history.back();
        }
      }
    };

    const handlePopState = (event) => {
      if (ignoreNextPopRef.current) {
        ignoreNextPopRef.current = false;
        return;
      }

      if (hasModalHistoryRef.current || getOpenModals().length > 0) {
        event.stopImmediatePropagation?.();
        event.preventDefault?.();
        hasModalHistoryRef.current = false;
        closeTopModal();
        window.setTimeout(() => {
          if (getOpenModals().length > 0) {
            window.history.pushState(
              { ...(window.history.state || {}), [MODAL_HISTORY_KEY]: true },
              "",
              lastPageUrlRef.current || window.location.href
            );
            hasModalHistoryRef.current = true;
          }
        }, 0);
      }
    };

    const observer = new MutationObserver(syncHistory);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "hidden", "aria-hidden", "open"],
    });

    window.addEventListener("popstate", handlePopState, { capture: true });
    syncHistory();

    return () => {
      observer.disconnect();
      window.removeEventListener("popstate", handlePopState, { capture: true });
    };
  }, [location.pathname]);
}
